// Cart Service for managing shopping cart functionality
export interface CartItem {
  id: number;
  title: string;
  price: number;
  image?: string;
  type: 'book' | 'package' | 'course';
  quantity: number;
}

class CartService {
  private static instance: CartService;
  private storageKey = 'mathknots_cart';

  static getInstance(): CartService {
    if (!CartService.instance) {
      CartService.instance = new CartService();
    }
    return CartService.instance;
  }

  // Get all cart items
  getCartItems(): CartItem[] {
    try {
      const cartData = localStorage.getItem(this.storageKey);
      return cartData ? JSON.parse(cartData) : [];
    } catch (error) {
      console.error('Error reading cart data:', error);
      return [];
    }
  }

  // Add item to cart
  addToCart(item: Omit<CartItem, 'quantity'>): boolean {
    try {
      const cartItems = this.getCartItems();
      const existingItemIndex = cartItems.findIndex(cartItem => 
        cartItem.id === item.id && cartItem.type === item.type
      );

      if (existingItemIndex >= 0) {
        // Item already exists, increase quantity
        cartItems[existingItemIndex].quantity += 1;
      } else {
        // Add new item
        cartItems.push({ ...item, quantity: 1 });
      }

      localStorage.setItem(this.storageKey, JSON.stringify(cartItems));
      this.notifyCartUpdate();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  }

  // Remove item from cart
  removeFromCart(id: number, type: 'book' | 'package' | 'course'): boolean {
    try {
      const cartItems = this.getCartItems();
      const filteredItems = cartItems.filter(item => 
        !(item.id === id && item.type === type)
      );
      
      localStorage.setItem(this.storageKey, JSON.stringify(filteredItems));
      this.notifyCartUpdate();
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  }

  // Update item quantity
  updateQuantity(id: number, type: 'book' | 'package' | 'course', quantity: number): boolean {
    try {
      const cartItems = this.getCartItems();
      const itemIndex = cartItems.findIndex(item => 
        item.id === id && item.type === type
      );

      if (itemIndex >= 0) {
        if (quantity <= 0) {
          return this.removeFromCart(id, type);
        } else {
          cartItems[itemIndex].quantity = quantity;
          localStorage.setItem(this.storageKey, JSON.stringify(cartItems));
          this.notifyCartUpdate();
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error updating cart quantity:', error);
      return false;
    }
  }

  // Get cart total
  getCartTotal(): number {
    const items = this.getCartItems();
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  }

  // Get cart item count
  getCartCount(): number {
    const items = this.getCartItems();
    return items.reduce((count, item) => count + item.quantity, 0);
  }

  // Clear entire cart
  clearCart(): boolean {
    try {
      localStorage.removeItem(this.storageKey);
      this.notifyCartUpdate();
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  }

  // Notify cart update (for UI updates)
  private notifyCartUpdate(): void {
    const cartData = {
      items: this.getCartItems(),
      count: this.getCartCount(),
      total: this.getCartTotal()
    };
    
    // Update cart badge in header if it exists
    this.updateCartBadge(cartData.count);
    
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('cartUpdated', {
      detail: cartData
    }));
    
    // Show toast notification
    this.showCartToast();
  }

  // Update cart badge in header
  private updateCartBadge(count: number): void {
    const cartBadge = document.querySelector('.cart-badge') as HTMLElement;
    if (cartBadge) {
      cartBadge.textContent = count.toString();
      cartBadge.style.display = count > 0 ? 'flex' : 'none';
    }
  }

  // Show cart toast notification
  private showCartToast(): void {
    // Remove existing toast
    const existingToast = document.querySelector('.cart-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create new toast
    const toast = document.createElement('div');
    toast.className = 'cart-toast';
    toast.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 12px 16px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;
        animation: slideIn 0.3s ease-out;
      ">
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
        </svg>
        Item added to cart!
      </div>
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(toast);

    // Remove toast after 3 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.style.animation = 'slideIn 0.3s ease-out reverse';
        setTimeout(() => {
          if (toast.parentNode) {
            toast.remove();
          }
        }, 300);
      }
    }, 3000);
  }

  // Initialize cart UI elements
  public initializeCartUI(): void {
    // Add cart badge to header if not exists
    const header = document.querySelector('header') || document.querySelector('.header');
    if (header && !document.querySelector('.cart-badge')) {
      const cartButton = document.createElement('div');
      cartButton.className = 'cart-button';
      cartButton.innerHTML = `
        <div style="
          position: relative;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.3s ease;
        " onmouseover="this.style.background='rgba(59, 130, 246, 0.1)'" 
           onmouseout="this.style.background='transparent'"
           onclick="window.cartService?.showCartSummary()">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
            <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l1.313 7h8.17l1.313-7H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div class="cart-badge" style="
            position: absolute;
            top: -2px;
            right: -2px;
            background: #ef4444;
            color: white;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            display: none;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: bold;
          ">0</div>
        </div>
      `;
      
      // Try to add to navigation area
      const nav = header.querySelector('nav') || header;
      nav.appendChild(cartButton);
    }

    // Initialize cart count
    this.updateCartBadge(this.getCartCount());
  }

  // Show cart summary modal
  public showCartSummary(): void {
    const items = this.getCartItems();
    const total = this.getCartTotal();
    
    if (items.length === 0) {
      alert('Your cart is empty');
      return;
    }

    const itemsList = items.map(item => 
      `• ${item.title} (x${item.quantity}) - $${(item.price * item.quantity).toFixed(2)}`
    ).join('\n');

    const message = `Shopping Cart:\n\n${itemsList}\n\nTotal: $${total.toFixed(2)}\n\nWould you like to proceed to checkout?`;
    
    if (window.confirm(message)) {
      window.alert('Checkout functionality coming soon!');
    }
  }
}

export const cartService = CartService.getInstance();
